import React, { useState, useEffect, useRef } from 'react';
import 'leaflet-control-geocoder';
import { Search, MapPin, X } from 'lucide-react';

interface LocationSearchBarProps {
	query: string;
	setQuery: (query: string) => void;
	onSelectLocation: (lat: number, lng: number) => void;
}

const LocationSearchBar: React.FC<LocationSearchBarProps> = ({ query, setQuery, onSelectLocation }) => {
	const [suggestions, setSuggestions] = useState<any[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [scrollState, setScrollState] = useState({ left: false, right: false });

	const checkScroll = () => {
		if (!inputRef.current) return;
		const { scrollLeft, scrollWidth, clientWidth } = inputRef.current;
		setScrollState({
			left: scrollLeft > 0,
			right: Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1,
		});
	};

	useEffect(() => {
		checkScroll();
	}, [query]);

	useEffect(() => {
		checkScroll();
		window.addEventListener('resize', checkScroll);
		return () => window.removeEventListener('resize', checkScroll);
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const fetchSuggestions = async (searchText: string) => {
		if (!searchText.trim()) {
			setSuggestions([]);
			return;
		}

		try {
			const res = await fetch(
				`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&limit=5`
			);
			const data = await res.json();
			const results = data.map((item: any) => ({
				name: item.display_name,
				center: { lat: parseFloat(item.lat), lng: parseFloat(item.lon) },
			}));
			setSuggestions(results);
			setSelectedIndex(0);
			setIsOpen(true);
		} catch (error) {
			console.error('Geocoding error:', error);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setQuery(value);

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}

		debounceTimeout.current = setTimeout(() => {
			fetchSuggestions(value);
		}, 300);
	};

	const handleSelect = (suggestion: any) => {
		setQuery(suggestion.name);
		setIsOpen(false);
		if (suggestion.center) {
			onSelectLocation(suggestion.center.lat, suggestion.center.lng);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!isOpen || suggestions.length === 0) return;

		const maxIndex = Math.min(suggestions.length, 4) - 1;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setSelectedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
				break;
			case 'Enter':
				e.preventDefault();
				handleSelect(suggestions[selectedIndex]);
				break;
			case 'Escape':
				e.preventDefault();
				setIsOpen(false);
				break;
		}
	};

	const clearQuery = () => {
		setQuery('');
		setSuggestions([]);
		setSelectedIndex(0);
		setIsOpen(false);
	};

	return (
		<div ref={wrapperRef} className='relative w-full mb-4' style={{ zIndex: 9999 }}>
			<div className='relative flex items-center'>
				<Search className='absolute left-[10px] top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
				<input
					ref={inputRef}
					type='text'
					onScroll={checkScroll}
					className='w-full pl-8 sm:pl-9 pr-8 sm:pr-10 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-full text-[13px] sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
					placeholder='Search for an address...'
					value={query}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={() => {
						if (suggestions.length > 0) setIsOpen(true);
					}}
				/>
				{scrollState.left && (
					<div className='absolute left-[34px] w-4 inset-y-1 bg-gradient-to-r from-white to-transparent pointer-events-none' />
				)}
				{scrollState.right && (
					<div className='absolute right-[38px] w-4 inset-y-1 bg-gradient-to-l from-white to-transparent pointer-events-none' />
				)}
				{query && (
					<button
						onClick={clearQuery}
						className='absolute right-[6px] top-1/2 -translate-y-1/2 px-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex items-center justify-center'
						style={{ height: 'calc(100% - 12px)' }}
						title='Clear search'
					>
						<X className='w-4 h-4' />
					</button>
				)}
			</div>

			{isOpen && suggestions.length > 0 && (
				<div
					className='absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto'
					style={{ zIndex: 10000 }}
				>
					{suggestions.slice(0, 4).map((suggestion, index) => (
						<div
							key={index}
							className={`flex items-start gap-2 px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
							onClick={() => handleSelect(suggestion)}
							onMouseEnter={() => setSelectedIndex(index)}
						>
							<MapPin className='w-4 h-4 mt-0.5 text-blue-500 shrink-0' />
							<span className='text-sm text-gray-700 line-clamp-2 leading-tight'>{suggestion.name}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default LocationSearchBar;
